import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Button, Collapse, Form, FormInstance, Switch, Tooltip, Typography, notification } from 'antd'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { faCloudCheck, faCopy, faTrash } from '@fortawesome/pro-duotone-svg-icons'

import { txt } from '../../../../locales/i18n'
import { formatDate } from '../../../../utils/date'
import * as CmsSectionService from '../../../../services/bb-api/cms/section'

import { DeleteModal } from '../../delete-modal'
import { getPanel } from '../panels'

import { getLayoutPanel } from '../layouts'
import type { Panel } from '../layouts/utils'

type Section = any

export type SectionFormProps = {
	visible: boolean
	selectedSection: Section
	setSelectedSectionId: (selectedSectionId: number | null) => void
	onSectionChange: (changedValues: any, values: any) => void
	onSectionUpdate: (sectionId: string, sectionUpdate: any) => void
	onSectionDeletion: (sectionId: string) => void
}

export const SectionForm = (props: SectionFormProps) => {
	const [isUpdating, setIsUpdating] = useState(false)
	const [isCopying, setIsCopying] = useState(false)
	const [deleteModelVisible, setDeleteModelVisible] = useState(false)

	const sectionId = props?.selectedSection?.id ?? null
	const layout = getLayoutPanel(props?.selectedSection?.layoutName ?? null)

	const formRef = useRef<FormInstance>(null)
	const { pageId } = useParams<{ pageId: string }>()

	useEffect(() => {
		const form = formRef.current
		if (form === null) return
		form.setFieldsValue(props?.selectedSection?.content)
	}, [props?.selectedSection])

	useEffect(() => {
		document.onkeydown = async (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 's') {
				event.preventDefault()

				const form = formRef.current
				if (form === null) return
				form.submit()
			}
		}

		return () => {
			document.onkeydown = () => null
		}
	}, [])

	const handleSaveSection = async (sectionValues: any) => {
		setIsUpdating(true)

		try {
			const sectionUpdate = await CmsSectionService.update(pageId, sectionId, sectionValues)
			props.onSectionUpdate(sectionId, sectionUpdate)
			notification.success({ message: txt('notification.update successful') })
		} catch (error) {
			notification.error({ message: txt('notification.update failed') })
		}

		setIsUpdating(false)
	}

	const copySection = async () => {
		if (sectionId === null) return
		setIsCopying(true)

		try {
			await CmsSectionService.copySection(pageId, sectionId)
			window.location.reload()
		} catch (error) {
			const message = txt('notification.create failed')
			notification.error({ message })
		}

		setIsCopying(false)
	}

	if (props.visible === false) return null

	return (
		<FormStyled
			ref={formRef}
			onFinish={handleSaveSection}
			onValuesChange={props.onSectionChange}
			initialValues={props.selectedSection.content}
			id="section-form"
		>
			<div className="layout-info-container">
				<div className="layout-info-top-container">
					<div className="layout-name-container">
						<label>{txt('cms.layout type')}</label>
						<Typography.Title level={2}>{layout?.layoutName}</Typography.Title>
					</div>

					<Button type="link" size="large" onClick={() => props.setSelectedSectionId(null)}>
						<FontAwesomeIcon icon={faArrowRight} />
					</Button>
				</div>

				<div className="layout-info-bottom-container">
					<div>
						<label>{txt('cms.created')}</label>
						{formatDate(new Date(props.selectedSection.created))}
					</div>

					<div>
						<label>{txt('cms.updated')}</label>
						<div>{formatDate(new Date(props.selectedSection.updated))}</div>
					</div>
				</div>
			</div>

			<Collapse ghost className="panels-container">
				{layout?.panels.map((panel: Panel) => {
					const Panel = getPanel(panel.type)
					if (Panel === null) return null

					return (
						<Collapse.Panel header={panel.label} key={panel.formKey} forceRender>
							<Panel propertyKey={panel.formKey} formRef={formRef} onSectionChange={props.onSectionChange} />
						</Collapse.Panel>
					)
				})}
			</Collapse>

			<div className="submit-container">
				<Tooltip title={txt('cms.published')}>
					<span className="release-switch-container">
						<Form.Item noStyle name="published" valuePropName="checked" initialValue={true}>
							<Switch />
						</Form.Item>
					</span>
				</Tooltip>

				<Button type="primary" ghost onClick={copySection} loading={isCopying}>
					<FontAwesomeIcon icon={faCopy} />
				</Button>

				<Button type="primary" htmlType="submit" loading={isUpdating}>
					<FontAwesomeIcon icon={faCloudCheck} />
				</Button>

				<Button danger type="primary" onClick={() => setDeleteModelVisible(true)}>
					<FontAwesomeIcon icon={faTrash} />
				</Button>
			</div>

			<DeleteModal
				onConfirm={() => props.onSectionDeletion(sectionId)}
				useVisible={[deleteModelVisible, setDeleteModelVisible]}
			/>
		</FormStyled>
	)
}

const FormStyled = styled(Form)`
	.layout-info-container {
		padding: 2ch;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		border-bottom: 1px solid #eee;
	}

	.layout-info-top-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.layout-info-bottom-container {
		display: flex;
		justify-content: space-between;
		align-items: center;

		> div {
			display: flex;
			flex-direction: column;
		}
	}

	.layout-name-container {
		display: flex;
		flex-direction: column;

		h2 {
			margin: 0;
		}
	}

	.panels-container {
		overflow-y: scroll;
	}

	.submit-container {
		border-top: 1px solid #eee;
		display: flex;
		gap: 1ch;
		padding-top: 1rem;
		align-items: center;
		margin-top: auto;
		padding: 2ch;

		.release-switch-container {
			margin-right: auto;
		}
	}
`
